import { ArrowDownTrayIcon, ShareIcon, XMarkIcon, CheckCircleIcon, StarIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/solid';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { useTwoColumnLayout } from '@components/layout/Layouts/TwoColumnLayout/TwoColumnLayoutContext';
import { ChatBubbleLeftEllipsisIcon, PhoneIcon } from '@heroicons/react/20/solid';

import { TailwindSpinner } from '@components/common/Elements/Spinner';
import { Fragment, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  CvDetailAvailableOperations,
  CvDetailAvailableOperationsCurrentStatus,
  CvDetailObject,
  CvDetailRegisteredActions,
} from '@components/templates/cv/interface/CvDetailInterface';
import { Button, Modal, Tooltip, Dropdown, DropdownButton, DropdownItems, DropdownItem } from '@components/common';
import { useBjModal } from '@hooks/common/useBjModal';
import { ShareModal } from '@components/features/modals/share-modal/ShareModal';
import { CustomConfirmModalContent } from '@components/templates/cv/partials/modals/CustomConfirmModalContent';
import { useTranslation } from 'next-i18next';
import { useStaticToasts } from '@hooks/useStaticToasts/useStaticToasts';
import { SendMessageModalContent } from '@components/templates/cv/partials/modals/SendMessageModalContent';
import { ResumeArrowsNavigation } from '@components/templates/cv/partials/navigation/ResumeArrowsNavigation';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { ApiCheckUnlock, fetcher, isApiV1Errors, isCheckUnlockStatusOk, ResponseWithHeaders } from '@utils/data-fetching';
import useWindowDimensions from '@hooks/useMediaQuery';
import { useAppDispatch, useAppSelector } from 'src/app';
import { AddressBookSolidIcon } from '@components/common/Icons';
import { useApplicantListsRedux } from '@components/templates/employer/cards-lists/hooks/useApplicantListsRedux';
import { ProfileOperationItemButton } from '@components/templates/cv/sections/ProfileOperationItemButton';
import { useSwrAvailableResumeOperations } from '@hooks/useSwrApi/endpoints/user';
import { useSwrInterviewRequest, useSwrGenerateInterviewQuestions, useSwrSaveInterviewQuestions } from '@hooks/useSwrApi/endpoints/interview';
import { filterAndOrderAvailableOperations } from '@components/templates/cv/utils';
import { InterviewQuestionsModal } from '@components/features/modals/interview-questions-modal';
import { AddCommentModalContent } from '@components/templates/cv/partials/modals/AddCommentModalContent';
import { AiEvaluationFeedbackModal } from '@components/features/modals/ai-evaluation-feedback/AiEvaluationFeedbackModal';
import { getCommentUnlockResponse } from '@store/reducers/employer/resume/resumeSelector';
import { clearCommentUnlockResponse } from '@store/reducers/employer/resume/resumeReducer';
import { EmployerResumeUnlockResponse } from '@type/redux';
import { useSwrResumeCheckUnlockPrice } from '@hooks/useSwrApi/endpoints/resume-operations/useSwrResumeCheckUnlockPrice';
import { showAddCompanyInfoModal } from '@components/features/modals/employer/AddCompanyInfoModal';
import { showRejectionReasonModal } from '@components/features/modals/employer/rejection-reason/RejectionReasonModal';
import { trackEvent } from '@services/tracking';
import { useAuthClient } from '@root/src/hooks';
import { useUnblockOperations } from '@components/templates/cv/hooks/useUnblockOperations';
import { InterfaceJobFolderOptions } from '@components/templates/cv/sections/ProfileHeading';
import { usePathname } from 'next/navigation';

export const UnblockActions = ({
  cvDetail,
  reFetchData,
  jobFolderId,
  isSinglePage,
  jobTitle,
  jobSlug,
  sendCurrentCvStatus,
  slug,
  setLoadingCvStatus,
  setTelephoneHighlight,
  setCurrentJobFolderHighlight,
  isFromJobContext,
  applicantSeenContact,
  applicantSeenContactStatus,
  closeCvModal,
  isCompany,
  rid,
  withNavbarInModal,
  locale,
  statusChangeFromModal,
  setJobFolderInfo,
  hasSourcingPackage = false,
  onRegisterDownloadCv,
  onRegisterActions,
  swrTalentList,
  openTalentModal,
  showLoadMore,
  size,
  setSize,
}: {
  cvDetail: CvDetailObject;
  setLoadingCvStatus: () => void;
  setJobFolderInfo: (jobFolderInfo: InterfaceJobFolderOptions) => void;
  statusChangeFromModal?: (statusId: string, slug: string) => void;
  setTelephoneHighlight: () => void;
  setCurrentJobFolderHighlight: () => void;
  jobSlug: string;
  jobTitle: string;
  isSinglePage?: boolean;
  slug: string;
  jobFolderId: number;
  isFromJobContext: boolean;
  applicantSeenContactStatus: boolean;
  reFetchData: (onSuccessCallback?: (cv: CvDetailObject) => void) => void;
  applicantSeenContact: () => void;
  sendCurrentCvStatus: (item: CvDetailAvailableOperationsCurrentStatus) => void;
  closeCvModal?: () => void;
  isCompany: boolean;
  rid?: string;
  withNavbarInModal?: boolean;
  locale: Locale;
  hasSourcingPackage?: boolean;
  onRegisterDownloadCv?: (fn: () => void) => void;
  onRegisterActions?: (actions: CvDetailRegisteredActions) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  swrTalentList?: any[];
  openTalentModal?: (slug: string, as?: string) => void;
  showLoadMore?: boolean;
  size?: number;
  setSize?: (size: number) => void;
}) => {
  const { t } = useTranslation('common');
  const userSlug = cvDetail.slug;
  const userIdToUnlock = cvDetail?.id as number;
  const [sendMessageModal, setSendMessageModal] = useState<boolean>(false);
  const [unlockedByCall, setUnlockedByCall] = useState<boolean>(false);
  const [currentCVStatus, setCurrentCVStatus] = useState<CvDetailAvailableOperationsCurrentStatus>();
  const [currentJobSlug, setCurrentJobSlug] = useState<string>('');
  const [interviewJobId, setInterviewJobId] = useState<number>(cvDetail?.application?.jobId || jobFolderId || 0);
  const [pendingInterviewRequest, setPendingInterviewRequest] = useState<boolean>(false);
  const telRef = useRef<HTMLAnchorElement>(null);
  const { status200Toast, showStatusToast } = useStaticToasts();
  const { isMobile } = useWindowDimensions();
  const headerInfo = useAppSelector((state) => state.user.headerInfo);
  const userId = headerInfo?.userId;
  const [isLoadingStatusChange, setIsLoadingStatusChange] = useState<boolean>(false);
  const [interviewRequestSent, setInterviewRequestSent] = useState<boolean>(false);
  const [isValidateFlowLoading, setIsValidateFlowLoading] = useState<boolean>(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [isReportEvaluationModalOpen, setIsReportEvaluationModalOpen] = useState(false);
  const lastScrollTop = useRef(0);

  useEffect(() => {
    const getScrollTarget = () => {
      if (!closeCvModal) {
        return window;
      }

      return Array.from(document.querySelectorAll<HTMLElement>('[data-modal-scroll-container]')).at(-1) ?? window;
    };

    const handleScroll = () => {
      const target = getScrollTarget();
      const scrollTop = target instanceof Window ? window.pageYOffset : target.scrollTop;
      if (scrollTop > lastScrollTop.current && scrollTop > 50) {
        setIsScrolling(true);
      } else {
        setIsScrolling(false);
      }
      lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
    };

    const scrollTarget = getScrollTarget();
    if (scrollTarget) {
      scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      if (scrollTarget) {
        scrollTarget.removeEventListener('scroll', handleScroll);
      }
    };
  }, [closeCvModal]);

  const { requestUnlockWithConfirm, confirmModalOpen, confirmUnlock, cancelUnlock } = useUnblockOperations({
    rid,
    userIdToUnlock,
    isCompany,
    jobSlug,
    jobTitle,
    jobFolderId,
    locale,
    userId,
    slug,
    ...(jobSlug && { jobSlug }),
  });

  // Interview request mutation
  const { trigger: triggerInterviewRequest, isMutating: isInterviewRequestLoading } = useSwrInterviewRequest({
    locale,
    jobSlug,
    userSlug,
    onSuccess: () => {
      setInterviewRequestSent(true);
      setIsValidateFlowLoading(false);
      status200Toast(t('cv.detail.action.interview_request_sent'));
      reFetchData();
    },
  });

  // Interview questions state and modal
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState<boolean>(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<string[]>([]);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState<boolean>(false);

  // Validation-cost confirmation modal — fires before the interview-request
  // flow for applicants on chargeable bundles (standard-fix / pro-fix). Mirrors
  // the popup shown in the bulk-validate flow (BulkValidate.tsx) so the user
  // sees the 1-credit charge before the reservation is created.
  const [isValidationCostModalOpen, setIsValidationCostModalOpen] = useState<boolean>(false);

  // Generate interview questions mutation
  const { trigger: triggerGenerateQuestions } = useSwrGenerateInterviewQuestions({
    locale,
    jobId: interviewJobId,
    onSuccess: (response) => {
      setGeneratedQuestions(response.questions);
      setIsQuestionsModalOpen(true);
      setIsGeneratingQuestions(false);
      setIsValidateFlowLoading(false);
    },
    onError: () => {
      setIsGeneratingQuestions(false);
      setIsValidateFlowLoading(false);
    },
  });

  // Save interview questions mutation
  const { trigger: triggerSaveQuestions, isMutating: isSavingQuestions } = useSwrSaveInterviewQuestions({
    locale,
    jobSlug,
    onSuccess: () => {
      setIsQuestionsModalOpen(false);
      // After successfully saving questions, run the validation-cost gate
      // inline. This is the SECOND time the gate may run for a chargeable
      // bundle: jobs that already had questions enabled hit the gate from
      // `handleInterviewRequest` directly; jobs without questions hit the
      // gate here, AFTER the questions are committed. Either way the cost
      // confirmation is the last step before triggerInterviewRequest fires
      // — no reservation row is ever created without the user agreeing to
      // the charge AND the job having questions saved.
      const validationCost = cvDetail?.application?.validationCost ?? 0;
      if (validationCost > 0) {
        setIsValidationCostModalOpen(true);
        return;
      }
      void triggerInterviewRequest();
    },
    onError: () => {
      // Error toast is already shown by the hook
    },
  });

  // Redux states for checking external operations response
  const commentUnlockResponse = useAppSelector(getCommentUnlockResponse);
  const dispatch = useAppDispatch();

  const { isAuth, redirectToLogin } = useAuthClient();

  // TwoColumnLayout context for sidebar toggle
  const twoColumnLayout = useTwoColumnLayout();
  const pathname = usePathname();
  // Check if the actual browser URL (not the intercepted route pathname) contains /mc
  // usePathname() in @detailModal parallel route returns /cv/[slug], not the MC URL
  const isMcUrl = (typeof window !== 'undefined' ? window.location.pathname.includes('/mc') : pathname?.includes('/mc')) ?? false;

  // Redux states for infinite lists. When in modal, with this we watch if:
  // - The applicant/talent status has changed (interview, shortlist, etc.).
  // - The CV was previously locked but now has been unlocked via message, show contact details, download, etc.
  // We will mutate the infinite list according to these states.
  // *************************************
  const [statusChanged, setStatusChanged] = useState<boolean>(false);
  const [lockedChanged, setLockedChanged] = useState<boolean>(false);
  const [isFreeCvInfoUnblock, setIsFreeCvInfoUnblock] = useState<boolean>(false);
  const [newStatusId, setNewStatusId] = useState<number | undefined>();
  useApplicantListsRedux({
    statusChanged,
    lockedChanged,
    slug,
    statusId: newStatusId,
  });

  // SWR mutation: Unlock for Message
  // ******************************************************
  const { trigger: triggerCheckUnlockPrice } = useSwrResumeCheckUnlockPrice({
    userSlug,
    locale,
    successCallback: (response) => {
      // If the response is false, the contact cost is 10 neuro
      setIsFreeCvInfoUnblock(response.is_unlock);
    },
  });

  // Handle share modal
  // *************************************
  const { isOpen: isShareModalOpen, toggleModal: toggleShareModal } = useBjModal();

  // SWR: Get available operations
  const { data: availableResumeOperations, mutate: refetchResumeOperations } = useSwrAvailableResumeOperations({
    locale,
    slug,
    shouldFetch: isAuth && isCompany,
    queryParams: {
      jobSlug: jobSlug?.length ? jobSlug : currentJobSlug,
      rid,
    },
    onSuccess: (response) => {
      setCurrentCVStatus(response?.currentStatus as CvDetailAvailableOperationsCurrentStatus);
      sendCurrentCvStatus(response?.currentStatus as CvDetailAvailableOperationsCurrentStatus);
    },
  });

  // Refetch available operations when conditions change (race condition fix)
  // This ensures data is fetched even if headerInfoData loads after component mount
  useEffect(() => {
    if (isAuth && isCompany && !availableResumeOperations) {
      void refetchResumeOperations();
    }
  }, [isAuth, isCompany, availableResumeOperations, refetchResumeOperations]);

  const downloadEndpoint = availableResumeOperations?.availableOperations.find((op) => op.operationId === 'download')?.operationEndpoint;

  // List of available operations filtered and ordered
  const availableOperations = useMemo(
    () => filterAndOrderAvailableOperations(availableResumeOperations?.availableOperations ?? []),
    [availableResumeOperations?.availableOperations],
  );

  const handleSuccessStatusChanged = useCallback(
    (response: EmployerResumeUnlockResponse) => {
      setCurrentJobSlug(response.response.jobSlug);
      // If the response status is success we need to re-fetch the available operations
      void refetchResumeOperations();
      sendGoogleTagEvent(`employer_cv_set_${response.operationName?.toLowerCase()}`, 'Custom');
      setIsLoadingStatusChange(false);
      setStatusChanged(true);
      setNewStatusId(response.statusId);
    },
    [refetchResumeOperations],
  );

  useEffect(() => {
    if (commentUnlockResponse) {
      dispatch(clearCommentUnlockResponse());
      handleSuccessStatusChanged(commentUnlockResponse);
    }
  }, [commentUnlockResponse, dispatch, handleSuccessStatusChanged]);

  useEffect(() => {
    // /check-unlock requires auth — skip for guests (401) and keep the
    // default locked/unlock buttons (isFreeCvInfoUnblock stays false).
    if (!isAuth) return;
    void triggerCheckUnlockPrice({ jobId: jobFolderId, title: jobTitle, jobSlug });
  }, [isAuth, triggerCheckUnlockPrice, jobFolderId, jobTitle, jobSlug]);

  useEffect(() => {
    if (!interviewJobId && cvDetail?.application?.jobId) {
      setInterviewJobId(cvDetail.application.jobId);
    } else if (!interviewJobId && jobFolderId) {
      setInterviewJobId(jobFolderId);
    }
  }, [interviewJobId, cvDetail?.application?.jobId, jobFolderId]);

  const sendContactedData = useCallback(() => {
    fetcher(`/v1/resume-operation/${userIdToUnlock}/contact${rid ? `?rid=${rid}` : ''}`, locale, {
      method: 'POST',
      payload: {
        type: 'contact',
        jobSlug,
      },
      stringifyPayload: true,
    })
      .then((response) => {
        if (isApiV1Errors(response)) {
          const { code } = response.errors[0];
          showStatusToast(String(`${code}`));
        } else if (!applicantSeenContactStatus) {
          applicantSeenContact();
          setTelephoneHighlight();
        }
      })
      .catch(() => {});
  }, [userIdToUnlock, rid, locale, jobSlug, showStatusToast, applicantSeenContactStatus, applicantSeenContact, setTelephoneHighlight]);

  const isPromoPrice = !!(cvDetail?.fullPrice && cvDetail?.price && cvDetail?.price < cvDetail?.fullPrice);

  // we use the fetcher version with headers, because we need to get the filename from the response headers
  const getDownloadedPDF = () => {
    const apiURL = rid ? `${downloadEndpoint}?rid=${rid}` : `${downloadEndpoint}`;
    fetcher<unknown>(apiURL, locale, {
      method: 'GET',
      responseType: 'blob',
      withResponseHeaders: true,
    })
      .then((res) => {
        // this is how you make the response headers available and let TS know about it (assert res as ResponseWithHeaders<T>)
        const { response, responseHeaders }: { response: BlobPart; responseHeaders: Headers } = res as ResponseWithHeaders<BlobPart>;
        const filename = responseHeaders.get('content-disposition')?.split('filename=')[1];
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || `${userSlug}.pdf`);
        document.body.appendChild(link);
        link.click();
        reFetchData();
        //   aici punem tracking event de download
        trackEvent({
          eventType: 'action',
          entityType: 'resume',
          entityId: slug,
          action: 'contact',
          viewContext: 'detail',
          ...(userId && { userId: userId.toString() }),
          ...(rid && { listUuid: rid }),
        });
      })
      .catch(() => {});
  };

  // Unlock with PDF if the confirmation modal is true
  const unlockWithDownloadPdf = () => {
    try {
      requestUnlockWithConfirm({
        type: 'download',
        onSuccess: (res) => {
          if (isCheckUnlockStatusOk(res)) {
            sendGoogleTagEvent('premium_contact_download', 'Custom');
            getDownloadedPDF();
            reFetchData();
            void refetchResumeOperations();
            setLockedChanged(true);
            if (res.meta?.consumptionInfoSummary) {
              status200Toast(res.meta.consumptionInfoSummary);
            }
          }
        },
      });
    } catch {
      console.warn('error');
    }
  };

  // If the CV in unlocked than we can download the PDF else we are going in the process
  const checkUnlockPdf = () => {
    if (cvDetail.locked === false) {
      getDownloadedPDF();
    } else {
      unlockWithDownloadPdf();
    }
  };

  // Keep a ref to the latest checkUnlockPdf so the stable wrapper below always
  // dispatches to the current closure (no stale `cvDetail.locked`).
  const checkUnlockPdfRef = useRef(checkUnlockPdf);
  checkUnlockPdfRef.current = checkUnlockPdf;

  // Register the download CV handler for use in ProfileHeading. The wrapper is
  // stable so that parent's `onRegisterDownloadCv` is called at most once per
  // mount — registering a fresh function each render would setState in the
  // parent and re-render us, causing an infinite loop.
  const registeredDownloadHandler = useCallback(() => {
    checkUnlockPdfRef.current();
  }, []);

  useEffect(() => {
    onRegisterDownloadCv?.(registeredDownloadHandler);
  }, [onRegisterDownloadCv, registeredDownloadHandler]);

  // Stable handlers for actions consumed outside UnblockActions (ProfileComments,
  // EvaluateBia). setState setters from useState already have stable identity,
  // so wrapping them in useCallback with [] gives us a stable reference for free.
  const handleAddComment = useCallback(() => setIsCommentModalOpen(true), []);
  const handleReportEvaluation = useCallback(() => setIsReportEvaluationModalOpen(true), []);

  // Memoize the registered actions so the object identity only changes when a
  // meaningful value does — otherwise the parent setter would run on every
  // render and trigger a re-render loop (see CvDetailHub#handleRegisterActions
  // which bails out on identical references).
  const registeredActions = useMemo<CvDetailRegisteredActions>(
    () => ({
      onAddComment: handleAddComment,
      onReportEvaluation: handleReportEvaluation,
      isFreeCvInfoUnblock,
    }),
    [handleAddComment, handleReportEvaluation, isFreeCvInfoUnblock],
  );

  useEffect(() => {
    onRegisterActions?.(registeredActions);
  }, [onRegisterActions, registeredActions]);

  const unlockWithMessage = () => {
    if (cvDetail.locked === false) {
      setSendMessageModal(true);
    } else {
      try {
        requestUnlockWithConfirm({
          type: 'message',
          onSuccess: (res) => {
            if (isCheckUnlockStatusOk(res)) {
              setCurrentJobFolderHighlight();
              setSendMessageModal(true);
              reFetchData();
              void refetchResumeOperations();
              sendGoogleTagEvent('premium_contact_message', 'Custom');
              setLockedChanged(true);
              if (res.meta?.consumptionInfoSummary) {
                status200Toast(res.meta.consumptionInfoSummary);
              }
            }
          },
        });
      } catch {
        console.warn('error');
      }
    }
  };

  const unlockContactData = useCallback(
    (onSuccessCallback?: (cv: CvDetailObject) => void) => {
      if (cvDetail?.privateInformation?.phone !== undefined && unlockedByCall) {
        setUnlockedByCall(false);
        if (onSuccessCallback) onSuccessCallback(cvDetail);
      } else {
        try {
          requestUnlockWithConfirm({
            type: 'contact',
            onSuccess: (res) => {
              if (isCheckUnlockStatusOk(res)) {
                const resolvedJobId = res.jobId || jobFolderId || cvDetail?.application?.jobId || 0;
                if (resolvedJobId && resolvedJobId !== interviewJobId) {
                  setInterviewJobId(resolvedJobId);
                }
                sendContactedData();
                setCurrentJobFolderHighlight();
                setUnlockedByCall(true);
                reFetchData(onSuccessCallback);
                void refetchResumeOperations();
                sendGoogleTagEvent('premium_contact_call', 'Custom');
                setTimeout(() => {
                  telRef?.current?.click();
                }, 750);
                setLockedChanged(true);
                if (res.meta?.consumptionInfoSummary) {
                  status200Toast(res.meta?.consumptionInfoSummary);
                }
              }
            },
          });
        } catch {
          console.warn('error');
        }
      }
    },
    [
      cvDetail,
      unlockedByCall,
      requestUnlockWithConfirm,
      jobFolderId,
      interviewJobId,
      sendContactedData,
      setCurrentJobFolderHighlight,
      reFetchData,
      refetchResumeOperations,
      status200Toast,
    ],
  );

  // Change status to specific operation, each item has the parameters in the available
  // action we need to get them out
  const applyStatusChange = useCallback(
    (params: CvDetailAvailableOperations) => {
      setIsLoadingStatusChange(true);
      const eventName = (params?.name ?? '').toLowerCase();
      const uId = params.operationParams.filter((x: { id: string }) => x.id === 'userId').map((item: { value: number }) => item.value)[0];
      const jobId = params.operationParams.filter((x: { id: string }) => x.id === 'jobId').map((item: { value: number }) => item.value)[0];
      const statusId = params.operationParams.filter((x: { id: string }) => x.id === 'status').map((item: { value: number }) => item.value)[0];

      // In case of CV view, if we have dropdown for job select we need to overwrite the job context,
      // so that the operation will be applied to job context
      const jobFinalId = jobId === undefined ? jobFolderId : jobId;

      fetcher<ApiCheckUnlock>(`${params.operationEndpoint}${rid ? `?rid=${rid}` : ''}`, locale, {
        method: 'POST',
        payload: {
          userId: uId,
          status: statusId,
          jobId: jobFinalId,
          title: jobTitle,
        },
        stringifyPayload: true,
      })
        .then((response) => {
          if (isCheckUnlockStatusOk(response)) {
            // aplicam tracking pentru status change
            if (statusId === 3) {
              trackEvent({
                eventType: 'action',
                entityType: 'resume',
                entityId: slug,
                action: 'interview',
                viewContext: 'detail',
                ...(userId && { userId: userId.toString() }),
                ...(rid && { listUuid: rid }),
              });
            }

            handleSuccessStatusChanged({
              response,
              operationName: eventName,
              statusId,
            });

            // Show rejection reason modal when rejecting a recommended applicant
            if (Number(statusId) === 4) {
              const matchingScore = cvDetail?.application?.evaluation?.score;
              const applicantId = cvDetail?.application?.applicantId;
              if (applicantId) {
                showRejectionReasonModal(locale, applicantId, matchingScore);
              }
            }

            statusChangeFromModal?.(statusId.toString(), slug);
          } else if (isApiV1Errors(response)) {
            const { code } = response.errors[0];
            if (code === 403) {
              const successCallback = () => applyStatusChange(params);
              showAddCompanyInfoModal(locale, successCallback);
              setIsLoadingStatusChange(false);
              return;
            }
            showStatusToast(String(`${code}`));
            setIsLoadingStatusChange(false);
          }
        })
        .catch(() => {});
    },
    [jobFolderId, rid, locale, jobTitle, showStatusToast, handleSuccessStatusChanged, statusChangeFromModal, slug, userId, cvDetail?.application],
  );

  // Handle interview request button click.
  //
  // Ordering invariant (matters for the chargeable-bundle paid-validation flow):
  //   1. If the job has no enabled interview questions, ALWAYS generate +
  //      collect them first via the questions modal. The cost gate then runs
  //      from `triggerSaveQuestions.onSuccess` — after questions are committed.
  //   2. If the job already has questions enabled, fire the cost gate here.
  //   3. The InterviewRequest API call (and the wallet reservation it triggers)
  //      is ALWAYS the last step. The user never agrees to the charge before
  //      knowing the questions are in place.
  const handleInterviewRequest = useCallback(() => {
    if (!cvDetail?.application?.enabledQuestions) {
      if (!interviewJobId) {
        setIsValidateFlowLoading(false);
        return;
      }
      // Generate interview questions first — the cost gate will run from
      // triggerSaveQuestions.onSuccess once the user saves them.
      setIsGeneratingQuestions(true);
      void triggerGenerateQuestions({});
      return;
    }

    // Questions are already enabled on the job → cost gate is the only thing
    // standing between us and the InterviewRequest call.
    const validationCost = cvDetail?.application?.validationCost ?? 0;
    if (validationCost > 0) {
      setIsValidationCostModalOpen(true);
      return;
    }
    void triggerInterviewRequest();
  }, [
    cvDetail?.application?.enabledQuestions,
    cvDetail?.application?.validationCost,
    triggerInterviewRequest,
    triggerGenerateQuestions,
    interviewJobId,
  ]);

  const handleValidationCostConfirm = useCallback(() => {
    setIsValidationCostModalOpen(false);
    void triggerInterviewRequest();
  }, [triggerInterviewRequest]);

  const handleValidationCostCancel = useCallback(() => {
    setIsValidationCostModalOpen(false);
    setIsValidateFlowLoading(false);
  }, []);

  useEffect(() => {
    if (pendingInterviewRequest && interviewJobId) {
      setPendingInterviewRequest(false);
      handleInterviewRequest();
    } else if (pendingInterviewRequest && !interviewJobId) {
      setPendingInterviewRequest(false);
    }
  }, [pendingInterviewRequest, interviewJobId, handleInterviewRequest]);

  // Handle saving interview questions
  const handleSaveQuestions = useCallback(
    (questions: string[]) => {
      void triggerSaveQuestions({ questions });
    },
    [triggerSaveQuestions],
  );

  // Handle closing the questions modal
  const handleCloseQuestionsModal = useCallback(() => {
    setIsQuestionsModalOpen(false);
    setGeneratedQuestions([]);
  }, []);

  // We need to check if the phone was unlocked by request, or it can make already call without
  // checking unlock
  useEffect(() => {
    if (cvDetail?.privateInformation?.phone !== undefined && unlockedByCall) {
      setUnlockedByCall(false);
    }
  }, [unlockedByCall, cvDetail]);

  const setLoadingCvStatusFromOperation = () => {
    setLoadingCvStatus();
  };

  const triggerPhoneLoad = useCallback(
    (event: MouseEvent) => {
      if (!isMobile) {
        event.preventDefault();
      }
      setTelephoneHighlight();
    },
    [isMobile, setTelephoneHighlight],
  );

  // Helper component
  // *****************************************************************************************************
  // Return the user status
  // *****************************************************************************************************
  const returnUserStatus = useCallback(() => {
    const defaultClasses =
      'text-pico lg:text-tiny uppercase text-ink flex items-center absolute shadow-xl bg-surface rounded-xl px-2 bottom-0 mt-2 text-nowrap';

    if (cvDetail?.online) {
      return (
        <span className={defaultClasses}>
          <span className="mr-2 block size-2.5 rounded-full bg-secondary" />
          {t('cv.detail.action.online.label')}
        </span>
      );
    }
    return null;
  }, [t, cvDetail?.online]);

  // Helper component
  // *****************************************************************************************************
  // Available operations buttons based on the current status and showing position (left right)
  // Left buttons are for the rejected status, right buttons are for the other statuses
  // *****************************************************************************************************
  const availableOperationsButtons = useCallback(
    (isLeftButton: boolean) => {
      if (!availableOperations?.length) {
        return null;
      }

      // Filter the operations based on the current status
      const filteredOperations = availableOperations.filter((item) => {
        const leftCondition =
          (item.operationId === 'status_2' && (currentCVStatus?.id === '4' || currentCVStatus?.id === '22')) || item.operationId === 'status_4';
        if (isLeftButton) return leftCondition;
        if (item.operationId === 'status_2') return false;
        return !leftCondition;
      });

      return (
        <>
          {filteredOperations.map((item) => (
            <div key={item.name}>
              <div>
                <div
                  role="presentation"
                  onClick={isLoadingStatusChange ? undefined : () => applyStatusChange(item)}
                  className={`mx-1 inline-flex cursor-pointer items-center justify-center gap-2 rounded-2xl bg-surface px-4.5 py-3.5 text-md font-medium text-ink transition-all duration-200 hover:bg-surface-100 hover:shadow-md ${isLoadingStatusChange ? 'pointer-events-none opacity-60' : ''}`}
                >
                  {isLoadingStatusChange && <TailwindSpinner className="!mx-0 size-5" />}
                  <ProfileOperationItemButton operationItem={item} currentCVStatus={currentCVStatus} withIcon />
                </div>
              </div>
            </div>
          ))}
        </>
      );
    },
    [isLoadingStatusChange, availableOperations, currentCVStatus, applyStatusChange],
  );

  // success data unlocked — OpenTok video-call hook removed in this PR;
  // the post-unlock branch that started a call is now a no-op. Generic
  // typed so callers (`onViewContactDataPressed`) keep passing `(cv,
  // withCall)` without TS arity errors and a future video-call hook
  // can drop in by replacing the body. Empty body intentional; no
  // unused-vars violation because the signature lives on the type
  // parameter, not on the implementation.
  const onSuccessContactDataUnlocked = useCallback<(cv: CvDetailObject, withCall?: boolean) => void>(() => {}, []);

  // Unlock contact data and show the phone number.
  // Show call options based on the `withCall` parameter
  const onViewContactDataPressed = useCallback(
    (event: MouseEvent | null, withCall = false, onSuccess?: (cv: CvDetailObject) => void) => {
      // Check if the user has a phone number, if not we need to unlock it
      if (cvDetail?.privateInformation?.phone === undefined && !unlockedByCall) {
        unlockContactData((cv) => {
          onSuccessContactDataUnlocked(cv, withCall);
          if (onSuccess) onSuccess(cv);
        });
        return;
      }
      if (!applicantSeenContactStatus) {
        sendContactedData();
        if (onSuccess) onSuccess(cvDetail);
        return;
      }
      if (event) {
        triggerPhoneLoad(event);
      }
      onSuccessContactDataUnlocked(cvDetail, withCall);
      if (onSuccess) onSuccess(cvDetail);
    },
    [cvDetail, unlockedByCall, unlockContactData, onSuccessContactDataUnlocked, applicantSeenContactStatus, sendContactedData, triggerPhoneLoad],
  );

  const handleValidateAction = useCallback(
    (event: MouseEvent | null) => {
      const isCvLocked = isCompany && cvDetail?.id !== userId && !cvDetail?.privateInformation?.phone && !cvDetail?.privateInformation?.email;

      if (isCvLocked) {
        void onViewContactDataPressed(event, false, () => {
          setPendingInterviewRequest(true);
        });
        return;
      }

      handleInterviewRequest();
    },
    [
      cvDetail?.id,
      cvDetail?.privateInformation?.phone,
      cvDetail?.privateInformation?.email,
      handleInterviewRequest,
      isCompany,
      onViewContactDataPressed,
      userId,
    ],
  );

  const handleValidateClick = useCallback(
    (event: MouseEvent) => {
      setIsValidateFlowLoading(true);
      handleValidateAction(event);
    },
    [handleValidateAction],
  );

  // Render main component
  // *****************************************************************************************************
  return (
    <>
      {/* Share modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={toggleShareModal}
        pathnameShareUrl={locale === 'ro' ? `/cv/${userSlug}` : `/${locale}/cv/${userSlug}`}
        ogTitle={t('cv.detail.meta.title')}
        ogDescription={`Bestjobs - ${t('cv.detail.meta.title')} - ${userSlug}`}
      />

      {isAuth && (
        <>
          <Modal
            size="sm"
            isOpen={confirmModalOpen}
            body={
              <CustomConfirmModalContent
                jobContextDetail={{
                  jobFolderId,
                  title: jobTitle,
                  jobSlug,
                }}
                updateActiveJobFolderContext={(item) => setJobFolderInfo(item)}
                userSlug={cvDetail?.slug}
                {...(cvDetail?.price !== undefined ? { defaultPrice: { price: Number(cvDetail.price), fullPrice: Number(cvDetail.fullPrice ?? 0) } } : {})}
                onSendSuccess={(val) => {
                  if (val) {
                    confirmUnlock();
                  } else {
                    cancelUnlock();
                    setIsValidateFlowLoading(false);
                  }
                }}
              />
            }
            onClose={() => null}
          />
          <Modal
            isOpen={sendMessageModal}
            body={
              <SendMessageModalContent
                imageUrl={cvDetail?.imgUrl ? cvDetail.imgUrl : ''}
                candidateName={cvDetail?.privateInformation?.name ? cvDetail.privateInformation.name : ''}
                onSendSuccess={() => setSendMessageModal(false)}
                onClose={() => {
                  setSendMessageModal(false);
                }}
                slug={slug}
                trackingId={rid}
                locale={locale}
              />
            }
            onClose={() => {
              setSendMessageModal(false);
            }}
          />

          {/* Interview Questions Modal */}
          <InterviewQuestionsModal
            isOpen={isQuestionsModalOpen}
            onClose={handleCloseQuestionsModal}
            questions={generatedQuestions}
            onSave={handleSaveQuestions}
            isSaving={isSavingQuestions}
          />

          {/* Validation Cost Confirmation Modal — appears before the interview-request
              flow for applicants whose job bundle charges for validation. */}
          <Modal
            isOpen={isValidationCostModalOpen}
            onClose={handleValidationCostCancel}
            size="sm"
            centered
          >
            <div className="border-b px-4 py-2 text-left">
              <span className="block text-xl font-bold">
                {t('cv.action.bulk.validate.unlock_title')}
              </span>
              <span className="text-sm text-ink-medium">{t('global.label.unlock.price.candidate.agree')}</span>
            </div>
            <div className="p-5">
              <div className="mb-5 text-center">
                <div className="w-full justify-center border-b py-3 pt-4 text-sm text-ink-medium">
                  {t('cv.action.bulk.validate.validation_description', { count: 1 })}
                  <div className="mt-2 flex items-end justify-center gap-1">
                    {`${t('cv.action.bulk.validate.validation_label')}:`}
                    <span className="text-[30px] font-bold leading-none text-secondary">
                      {cvDetail?.application?.validationCost ?? 0}
                    </span>
                    <span className="text-base font-medium text-secondary">
                      {t('global.label.credits')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <Button
                  role="presentation"
                  size="sm"
                  color="error"
                  onClick={handleValidationCostCancel}
                  className="mr-2"
                >
                  {t('global.label.unlock.candidate.deny')}
                </Button>
                <Button
                  role="presentation"
                  size="sm"
                  color="secondary"
                  onClick={handleValidationCostConfirm}
                >
                  {t('global.label.unlock.candidate.info')}
                </Button>
              </div>
            </div>
          </Modal>

          {/* Add Comment Modal */}
          <Modal isOpen={isCommentModalOpen} onClose={() => setIsCommentModalOpen(false)} size="sm" centered>
            <Modal.Header onClose={() => setIsCommentModalOpen(false)}>
              <p className="text-lg text-ink">{t('cv.action.comment.add')}</p>
            </Modal.Header>
            <Modal.Body>
              <AddCommentModalContent cvUserId={userIdToUnlock} userSlug={slug} locale={locale} onClose={() => setIsCommentModalOpen(false)} />
            </Modal.Body>
          </Modal>

          {/* Report Evaluation Modal */}
          {cvDetail?.application?.evaluation && cvDetail?.application?.applicantId && (
            <AiEvaluationFeedbackModal
              isOpen={isReportEvaluationModalOpen}
              toggleModal={() => setIsReportEvaluationModalOpen(false)}
              identifier={cvDetail.application.applicantId.toString()}
              applicantEvaluation={cvDetail.application.evaluation}
            />
          )}
        </>
      )}

      {/* Available operations — recruiter-targeted action bar (status change, contact, validate).
          Hidden when the user is viewing their own CV via "View profile". */}
      {isAuth && !cvDetail?.isOwnCV && (
        <section
          className={clsx(
            'fixed bottom-4 z-30 flex -translate-x-1/2 flex-col items-center gap-2',
            'pointer-events-none',
            'left-1/2 lg:left-1/4',
             
            twoColumnLayout && twoColumnLayout.isLeftOpen ? 'lg:left-[40%]' : twoColumnLayout && !twoColumnLayout.isLeftOpen ? 'lg:left-1/4' : '',
          )}
        >
          <div
            className={clsx(
              // Single-page CV detail shows the MC mission chips fixed at the bottom; lift the
              // actions bar above them (chips sit at bottom-3 / lg:bottom-8) so they don't overlap.
              isSinglePage ? 'mb-20 lg:mb-24' : 'mb-4',
              'pointer-events-auto flex items-center gap-2 rounded-[24px] border border-input bg-surface px-4 py-[14px] shadow-lg backdrop-blur-sm lg:gap-5 lg:px-7',
            )}
          >
            {/* Status change (reject/unreject). Add Comment moved to ProfileComments;
                  Report Evaluation moved to EvaluateBia. Remaining items collapse
                  to a single inline button when only one status operation applies,
                  otherwise keep the overflow menu. */}
            {isCompany &&
              (() => {
                const statusOperations =
                  availableOperations?.filter(
                    (item) =>
                      (item.operationId === 'status_2' && (currentCVStatus?.id === '4' || currentCVStatus?.id === '22')) || item.operationId === 'status_4',
                  ) ?? [];

                if (statusOperations.length === 0) return null;

                if (statusOperations.length === 1) {
                  const item = statusOperations[0];
                  return (
                    <Button
                      key={item.name}
                      styling="none"
                      onClick={isLoadingStatusChange ? undefined : () => applyStatusChange(item)}
                      className={clsx(
                        'mx-1 inline-flex items-center justify-center gap-2 rounded-2xl px-4.5 py-3.5 text-md font-medium text-ink transition-all duration-200',
                        'bg-surface-100 hover:bg-surface-200 hover:shadow-md',
                        isLoadingStatusChange && 'pointer-events-none opacity-60',
                      )}
                    >
                      {isLoadingStatusChange && <TailwindSpinner className="!mx-0 size-5" />}
                      <ProfileOperationItemButton operationItem={item} currentCVStatus={currentCVStatus} withIcon />
                    </Button>
                  );
                }

                return (
                  <Dropdown>
                    <DropdownButton
                      as={Button}
                      styling="none"
                      className="relative mx-1 flex h-12 items-center justify-center rounded-2xl bg-surface px-3 text-md font-medium text-ink transition-all duration-200 hover:bg-surface-100 hover:shadow-md"
                    >
                      <EllipsisHorizontalIcon className="size-6" />
                    </DropdownButton>

                    <DropdownItems animation="slideUp" anchor="top start" className="z-[100] mb-2 flex min-w-48 flex-col items-start gap-1 !rounded-2xl p-2">
                      {statusOperations.map((item) => (
                        <DropdownItem key={item.name} as={Fragment}>
                          <div
                            role="presentation"
                            onClick={() => applyStatusChange(item)}
                            className="flex w-full cursor-pointer items-center justify-center rounded-xl bg-surface px-3 py-2 text-md font-medium text-ink transition-all duration-200 hover:bg-surface-100 disabled:opacity-60"
                          >
                            <ProfileOperationItemButton operationItem={item} currentCVStatus={currentCVStatus} withIcon />
                          </div>
                        </DropdownItem>
                      ))}
                    </DropdownItems>
                  </Dropdown>
                );
              })()}

            {/* Contact dropdown -  call, message, view */}
            <Dropdown>
              {/* button to view options */}
              <DropdownButton
                as={Button}
                color="secondary"
                className="relative mx-1 flex h-12 min-w-32 items-center justify-center rounded-2xl !text-md text-white hover:!bg-secondary/90"
              >
                <>
                  <span>{t('cv.detail.action.contact.label')}</span>

                  <div className="absolute -bottom-2 left-1/2 flex -translate-x-1/2 flex-col items-center">{returnUserStatus()}</div>
                </>
              </DropdownButton>

              {/* Dropdown list */}
              <DropdownItems
                animation="slideUp"
                className="bottom-full mb-2 flex min-w-48 flex-col items-start gap-3 !rounded-2xl border-t-8 border-secondary p-4"
              >
                {/* Direct call - only for online users */}
                {(isFreeCvInfoUnblock || cvDetail?.locked) && (
                  <div className="absolute -top-3.5 left-1/2 min-w-max -translate-x-1/2 whitespace-nowrap rounded-lg bg-secondary px-1 text-pico font-semibold text-surface">
                    {isFreeCvInfoUnblock && <span>{t('cv.detail.action.contact.free')}</span>}

                    {cvDetail?.locked === true &&
                      (hasSourcingPackage ? (
                        <span>{t('sourcing.contact.badge')}</span>
                      ) : (
                        <>
                          {`${t('cv.detail.contact.label')}:`}
                          {isPromoPrice && <span className="ml-1 text-danger line-through">{cvDetail?.fullPrice}</span>}
                          <span className="ml-1">
                            {cvDetail?.price} {t('global.label.credits')}
                          </span>
                        </>
                      ))}
                  </div>
                )}

                <DropdownItem
                  as={Button}
                  styling="text"
                  color="ink"
                  className="block md:hidden"
                  onClick={(event) => {
                    void onViewContactDataPressed(event, true);
                  }}
                >
                  <PhoneIcon className="mr-2 size-4" />
                  <span className="">{t('cv.detail.action.call_direct.label')}</span>
                </DropdownItem>

                {isCompany && cvDetail?.id !== userId && (
                  <DropdownItem as={Button} styling="text" color="ink" onClick={() => checkUnlockPdf()}>
                    {/* Download CV button */}
                    <>
                      <ArrowDownTrayIcon className="mr-2 size-5 text-ink" />
                      {t('global.action.download.label')}
                    </>
                  </DropdownItem>
                )}

                {/* Send message */}
                <DropdownItem as={Button} styling="text" color="ink" onClick={() => unlockWithMessage()}>
                  <ChatBubbleLeftEllipsisIcon className="mr-2 size-4" />
                  <span className="flex items-center">
                    {t('cv.detail.action.message.label')}
                    {cvDetail?.hasUnreadMessage && <span className="ml-2 block size-2.5 rounded-2xl bg-error" />}
                  </span>
                </DropdownItem>

                {/* View contact data */}
                <DropdownItem
                  as={Button}
                  styling="text"
                  color="ink"
                  onClick={(event) => {
                    void onViewContactDataPressed(event, true);
                  }}
                >
                  <div>
                    <AddressBookSolidIcon className="mr-2.5 h-4 w-3.5 fill-ink" />
                  </div>
                  <span className="">{t('cv.detail.action.see_contact.label')}</span>
                </DropdownItem>
              </DropdownItems>
            </Dropdown>

            {/* Interview request button - conditional rendering based on eligibleForInterviewRequest and status */}
            {(() => {
              const eligibleForInterviewRequest = cvDetail?.application?.eligibleForInterviewRequest;
              const interviewRequestStatus = cvDetail?.application?.interviewRequest?.status;

              // Determine if we should show the "requested" state
              const showRequestedState = interviewRequestSent || (eligibleForInterviewRequest === false && interviewRequestStatus != null);

              // Hide button if eligibleForInterviewRequest is false and has no status
              const shouldHideButton = eligibleForInterviewRequest === false && interviewRequestStatus == null;

              if (shouldHideButton) {
                return null;
              }

              // Determine button content based on state and device
              let buttonContent;
              if (showRequestedState && isMobile) {
                // Show "requested" state with star icon (mobile)
                buttonContent = <StarIcon className="size-5" />;
              } else if (showRequestedState) {
                // Show "requested" state with star icon + text (desktop)
                buttonContent = (
                  <>
                    <span>{t('cv.detail.action.validate.requested')}</span>
                  </>
                );
              } else if (isMobile) {
                // Show normal state (mobile)
                buttonContent = <CheckCircleIcon className="size-5" />;
              } else {
                // Show normal state (desktop)
                buttonContent = <span>{t('cv.detail.action.validate')}</span>;
              }

              // Requirement 1: hide the validation button if validation is pending
              if (cvDetail?.application?.interviewRequest !== undefined || interviewRequestSent) {
                return null;
              }
              return (
                <Button
                  id="cvDetailValidateButton"
                  styling="none"
                  className={`relative mx-1 flex items-center justify-center rounded-2xl px-4.5 py-3.5 !text-md transition-all duration-200 hover:shadow-md disabled:bg-warning/20 disabled:!text-orange-600 disabled:opacity-60 md:h-12 md:min-w-32 ${
                    showRequestedState ? 'bg-warning/20 !text-warning hover:bg-warning/30' : 'bg-surface !text-ink hover:bg-surface-100'
                  }`}
                  onClick={handleValidateClick}
                  isLoading={isValidateFlowLoading || isInterviewRequestLoading || isGeneratingQuestions}
                >
                  {buttonContent}
                </Button>
              );
            })()}

            {/* Right buttons for resume operations (other than rejected) */}
            {availableOperationsButtons(false)}
          </div>
        </section>
      )}

      {/* Guest action bar — mirrors the authed bar but offers a single
          sign-in CTA that returns to this CV after login. */}
      {!isAuth && !cvDetail?.isOwnCV && (
        <section
          className={clsx(
            'fixed bottom-4 z-30 flex -translate-x-1/2 flex-col items-center gap-2',
            'pointer-events-none',
            'left-1/2 lg:left-1/4',
            twoColumnLayout && twoColumnLayout.isLeftOpen ? 'lg:left-[40%]' : twoColumnLayout && !twoColumnLayout.isLeftOpen ? 'lg:left-1/4' : '',
          )}
        >
          <div
            className={clsx(
              // Single-page CV detail shows the MC mission chips fixed at the bottom; lift the
              // actions bar above them (chips sit at bottom-3 / lg:bottom-8) so they don't overlap.
              isSinglePage ? 'mb-20 lg:mb-24' : 'mb-4',
              'pointer-events-auto flex items-center gap-2 rounded-[24px] border border-input bg-surface px-4 py-[14px] shadow-lg backdrop-blur-sm lg:gap-5 lg:px-7',
            )}
          >
            <Button
              color="secondary"
              className="relative mx-1 flex h-12 items-center justify-center rounded-2xl !text-md text-white hover:!bg-secondary/90"
              onClick={() => redirectToLogin(typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : undefined)}
            >
              {t('cv.detail.action.signin_to_contact', 'Sign in to contact this user')}
            </Button>
          </div>
        </section>
      )}

      {/* Profile navigation and language selector - sticky only in modal */}
      <div
        className={clsx(
          'sticky z-1 self-start border-y bg-surface-100 py-2 transition-all duration-300',
          // On a standalone page where the scroll container already sits below the
          // fixed header (TwoColumnLayout's <main>), stick at top-0. Otherwise
          // (legacy single page where the body scrolls under the fixed header, or
          // a modal that includes an in-modal navbar) stick below the header.
          withNavbarInModal || (isSinglePage && !twoColumnLayout) ? 'top-stickUnderHeader' : 'top-0',
          !closeCvModal && !twoColumnLayout && !isMcUrl && 'hidden',
          isScrolling ? 'pointer-events-none -translate-y-full opacity-0' : 'translate-y-0 opacity-100',
        )}
      >
        {/* Align the navigation bar with the second column (Profile Content) */}
        <div className="w-full px-4 lg:pl-8 lg:pr-16">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center justify-start gap-1.5 md:gap-4">
              {/* Sidebar toggle button - only on mobile, when TwoColumnLayout context is available */}
              {(twoColumnLayout || isMcUrl) && (
                <Button
                  styling="none"
                  data-sidebar-toggle
                  onClick={twoColumnLayout ? twoColumnLayout.toggleLeftSidebar : () => window.dispatchEvent(new Event('toggle-sidebar'))}
                  aria-label={twoColumnLayout?.isLeftOpen ? 'Close sidebar' : 'Open sidebar'}
                  className="lg:hidden"
                >
                  <span className="flex size-7 items-center justify-center rounded-full bg-surface md:size-8">
                    <Bars3Icon className="size-5 text-ink" />
                  </span>
                </Button>
              )}

              {
                /* Close modal button */
                closeCvModal && (
                  <Tooltip tooltipText={t('global.btn.close')} position="bottom" colorScheme="default">
                    <Button styling="none" onClick={closeCvModal} aria-label={t('global.btn.close')}>
                      <span className="flex size-7 items-center justify-center rounded-full bg-surface md:size-8">
                        <XMarkIcon className="size-5 text-ink" />
                      </span>
                    </Button>
                  </Tooltip>
                )
              }

              {/* Share CV button */}
              <Tooltip tooltipText={t('cv.action.share')} position="bottom" colorScheme="default">
                <Button
                  styling="none"
                  aria-label={t('cv.action.share')}
                  onClick={toggleShareModal}
                  className="flex size-7 items-center justify-center rounded-full bg-surface md:size-8"
                >
                  <ShareIcon className="size-4 text-ink" />
                </Button>
              </Tooltip>
            </div>

            {/* This component return the arrows to the navigation between items. While we do not have the list in Next.js, we are storing in cookie */}
            {/* the value of the last search (filters), then in react we are getting a list of CV-s for that search. We are sending also the offset */}
            {/* and then manipulate each case in the component */}

            <div>
              <ResumeArrowsNavigation
                setLoadingCvStatusFromOperation={setLoadingCvStatusFromOperation}
                jobSlug={jobSlug}
                isFromJobContext={isFromJobContext}
                slug={slug}
                swrTalentList={swrTalentList}
                openTalentModal={openTalentModal}
                showLoadMore={showLoadMore}
                size={size}
                setSize={setSize}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
